/**
 * Survey Template branching editor — extracted from TemplateEditor.tsx.
 *
 * Contains the Notion-style nested And/Or condition builder:
 *   - BranchingEditor: entry point bound to a Question's visibility tree
 *   - GroupNode: recursive editor for rules + nested condition groups
 *
 * Extracted into its own module so Babel's AST traversal does not overflow
 * on the (large) TemplateEditor.tsx file.
 */
import React from 'react';
import { Input, Label } from '@/components/ui/input';
import { GitBranch, Plus, Trash2 } from 'lucide-react';

// ---------- Types ----------
export type VNode =
  | { kind: 'rule'; question_code: string; op: string; value?: unknown }
  | { kind: 'group'; logic: 'all' | 'any'; children: VNode[] };

// Minimal structural shapes needed by the branching editor.
export interface BranchQuestion {
  question_code: string;
  display_number: string;
  text: string;
  type: string;
  options?: string[];
  visibility_tree?: VNode | null;
  visibility_logic?: 'all' | 'any';
  conditions?: Array<{ question_code: string; op: string; value: unknown }>;
}

export interface BranchPatch {
  visibility_tree?: VNode | null;
  conditions?: Array<{ question_code: string; op: string; value: unknown }>;
  condition?: null;
}

export const OP_LABELS: Record<string, string> = {
  eq: 'equals', ne: 'not equal to', in: 'in list', not_in: 'not in list',
  gt: '>', gte: '>=', lt: '<', lte: '<=',
  contains: 'contains', answered: 'is answered', unanswered: 'is unanswered',
};

export const OP_NEEDS_VALUE = (op: string): boolean => !['answered', 'unanswered'].includes(op);

// -------------------------------------------------------------- Entry
export const BranchingEditor: React.FC<{
  q: BranchQuestion;
  priorQuestions: BranchQuestion[];
  branchOps: string[];
  onChange: (patch: BranchPatch) => void;
  disabled?: boolean;
  testIdx: string;
}> = ({ q, priorQuestions, branchOps, onChange, disabled, testIdx }) => {
  const root: VNode = (q.visibility_tree && q.visibility_tree.kind === 'group')
    ? q.visibility_tree
    : { kind: 'group', logic: q.visibility_logic || 'all', children: (q.conditions || []).map((c) => ({ kind: 'rule', ...c } as VNode)) };
  const targets = priorQuestions.filter((p) => p.question_code !== q.question_code);
  const commit = (next: VNode): void => onChange({ visibility_tree: next, conditions: [], condition: null });

  return (
    <div className="grid gap-2 rounded-lg border border-border bg-white p-3 shadow-sm" data-testid={`branching-panel-${testIdx}`}>
      <div className="flex items-center gap-2">
        <GitBranch size={15} className="text-raptor-blue" />
        <Label className="text-sm font-semibold text-raptor-navy">Show this question only when…</Label>
      </div>
      <GroupNode node={root} onNodeChange={commit} targets={targets} branchOps={branchOps} disabled={disabled} isRoot testIdx={`${testIdx}-0`} />
    </div>
  );
};

// -------------------------------------------------------------- Recursive group
const GroupNode: React.FC<{
  node: VNode; onNodeChange: (n: VNode) => void; onRemove?: () => void;
  targets: BranchQuestion[]; branchOps: string[]; disabled?: boolean; isRoot?: boolean; testIdx: string;
}> = ({ node, onNodeChange, onRemove, targets, branchOps, disabled, isRoot, testIdx }) => {
  const Self = GroupNode; // alias avoids visual-edits babel plugin recursing on self-reference
  const g = node.kind === 'group' ? node : { kind: 'group' as const, logic: 'all' as const, children: [] as VNode[] };
  const children = g.children || [];
  const setChild = (i: number, c: VNode): void => onNodeChange({ ...g, children: children.map((x, idx) => idx === i ? c : x) });
  const removeChild = (i: number): void => onNodeChange({ ...g, children: children.filter((_, idx) => idx !== i) });
  const newRule = (): VNode => ({ kind: 'rule', question_code: targets[0]?.question_code ?? '', op: 'eq', value: '' });
  const addRule = (): void => onNodeChange({ ...g, children: [...children, newRule()] });
  const addGroup = (): void => onNodeChange({ ...g, children: [...children, { kind: 'group', logic: 'all', children: [newRule()] }] });

  const ruleValue = (c: Extract<VNode, { kind: 'rule' }>, i: number): React.ReactNode => {
    if (!OP_NEEDS_VALUE(c.op)) return <div className="flex-1 text-xs text-raptor-navy/40 italic px-2">(no value)</div>;
    const tq = targets.find((p) => p.question_code === c.question_code);
    const opts = tq ? (tq.type === 'yes_no' ? ['yes', 'no'] : tq.type === 'yes_no_na' ? ['yes', 'no', 'na'] : (tq.options && tq.options.length ? tq.options : null)) : null;
    return opts ? (
      <select className="flex-1 min-w-0 rounded-md border border-border/60 bg-white px-2 py-1.5 text-xs font-body"
        value={c.value == null ? '' : String(c.value)} onChange={(e) => setChild(i, { ...c, value: e.target.value })} disabled={disabled} data-testid={`branch-value-${testIdx}-${i}`}>
        <option value="">Enter a value</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <Input value={c.value == null ? '' : String(c.value)} onChange={(e) => setChild(i, { ...c, value: e.target.value })} disabled={disabled} className="flex-1 min-w-0 text-xs h-auto py-1.5" placeholder="Enter a value" data-testid={`branch-value-${testIdx}-${i}`} />
    );
  };

  return (
    <div className={`grid gap-1.5 ${isRoot ? '' : 'rounded-md border border-raptor-blue/30 bg-raptor-blue/5 p-2'}`} data-testid={`branch-group-${testIdx}`}>
      {!isRoot && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-raptor-navy/60">Group — match</span>
          <select className="rounded-md border border-raptor-blue/40 bg-white px-1.5 py-1 text-xs font-semibold" value={g.logic} onChange={(e) => onNodeChange({ ...g, logic: e.target.value as 'all' | 'any' })} disabled={disabled} data-testid={`branch-glogic-${testIdx}`}>
            <option value="all">ALL (and)</option>
            <option value="any">ANY (or)</option>
          </select>
          {!disabled && onRemove && (
            <button type="button" onClick={onRemove} className="ml-auto p-1 rounded hover:bg-red-50 text-raptor-navy/40 hover:text-red-500" data-testid={`branch-group-remove-${testIdx}`}><Trash2 size={13} /></button>
          )}
        </div>
      )}
      {children.length === 0 && <div className="text-xs text-raptor-navy/55 italic px-1">No conditions — always shown.</div>}
      {children.map((child, i) => (
        <div key={`c-${testIdx}-${i}`} className="flex items-start gap-1.5">
          <div className="w-[64px] shrink-0 pt-1.5">
            {i === 0 ? <span className="text-xs font-semibold text-raptor-navy/60 pl-1">Where</span>
              : i === 1 ? (
                <select className="w-full rounded-md border border-raptor-blue/40 bg-white px-1 py-1 text-xs font-semibold" value={g.logic} onChange={(e) => onNodeChange({ ...g, logic: e.target.value as 'all' | 'any' })} disabled={disabled} data-testid={`branch-logic-${testIdx}`}>
                  <option value="all">and</option>
                  <option value="any">or</option>
                </select>
              ) : <span className="text-xs text-raptor-navy/50 pl-1">{g.logic === 'any' ? 'or' : 'and'}</span>}
          </div>
          {child.kind === 'group' ? (
            <div className="flex-1 min-w-0">
              <Self node={child} onNodeChange={(c) => setChild(i, c)} onRemove={() => removeChild(i)} targets={targets} branchOps={branchOps} disabled={disabled} testIdx={`${testIdx}-${i}`} />
            </div>
          ) : (
            <div className="flex-1 min-w-0 flex items-center gap-1.5" data-testid={`branch-rule-${testIdx}-${i}`}>
              <select className="flex-1 min-w-0 rounded-md border border-border/60 bg-white px-2 py-1.5 text-xs font-body" value={child.question_code} onChange={(e) => setChild(i, { ...child, question_code: e.target.value })} disabled={disabled} data-testid={`branch-target-${testIdx}-${i}`}>
                {targets.length === 0 && <option value="">(no earlier questions)</option>}
                {targets.map((p) => <option key={p.question_code} value={p.question_code}>{p.display_number} — {p.text.slice(0, 40)}</option>)}
              </select>
              <select className="w-[118px] shrink-0 rounded-md border border-border/60 bg-white px-2 py-1.5 text-xs font-body" value={child.op} onChange={(e) => setChild(i, { ...child, op: e.target.value })} disabled={disabled} data-testid={`branch-op-${testIdx}-${i}`}>
                {branchOps.map((op) => <option key={op} value={op}>{OP_LABELS[op] ?? op}</option>)}
              </select>
              {ruleValue(child, i)}
              {!disabled && <button type="button" onClick={() => removeChild(i)} className="shrink-0 p-1.5 rounded hover:bg-red-50 text-raptor-navy/40 hover:text-red-500" data-testid={`branch-remove-${testIdx}-${i}`}><Trash2 size={14} /></button>}
            </div>
          )}
        </div>
      ))}
      {!disabled && (
        <div className="flex items-center gap-4 pt-1 pl-1">
          <button type="button" onClick={addRule} disabled={targets.length === 0} className="flex items-center gap-1 text-xs font-semibold text-raptor-blue hover:underline disabled:opacity-40" data-testid={`add-branch-rule-${testIdx}`}><Plus size={13} /> Add condition</button>
          <button type="button" onClick={addGroup} disabled={targets.length === 0} className="flex items-center gap-1 text-xs font-semibold text-raptor-navy/70 hover:underline disabled:opacity-40" data-testid={`add-branch-group-${testIdx}`}><Plus size={13} /> Add condition group</button>
        </div>
      )}
    </div>
  );
};
